/**
 * Error Reporting Service
 *
 * Centralizes error logging, reporting, and handling throughout the application.
 * Integrates with Sentry for production error tracking.
 */
import * as Sentry from "@sentry/react";

import { ApiError } from "./api";

// Environment check
const isProduction = process.env.NODE_ENV === "production";

// Define a more specific type for the context if possible, otherwise use Record
type ErrorContext = Record<string, unknown> | { componentStack?: string };

/**
 * Initialize error reporting services
 */
export const initErrorReporting = (): void => {
  if (isProduction && process.env.REACT_APP_SENTRY_DSN) {
    Sentry.init({
      dsn: process.env.REACT_APP_SENTRY_DSN,
      environment: process.env.REACT_APP_ENVIRONMENT || "production",
      // Performance monitoring settings
      tracesSampleRate: 0.5,
    });
  }
};

/**
 * Log an error to the console and Sentry
 */
export const logError = (error: Error, context?: Record<string, unknown>): void => {
  // Always log to console for debugging
  console.error("[Error]", error);

  // Report to Sentry in production
  if (isProduction) {
    if (context) {
      Sentry.withScope((scope) => {
        Object.entries(context).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
        Sentry.captureException(error);
      });
    } else {
      Sentry.captureException(error);
    }
  }
};

/**
 * User-friendly error messages for common errors
 */
export const getUserFriendlyMessage = (error: Error): string => {
  if (error instanceof ApiError) {
    // Handle API errors
    switch (error.status) {
      case 401:
        return "Please sign in to continue";
      case 403:
        return "You don't have permission to access this resource";
      case 404:
        return "The requested resource was not found";
      case 500:
        return "An unexpected server error occurred. Please try again later";
      default:
        return error.message || "An error occurred with your request";
    }
  }

  // Fallback for general errors
  return "Something went wrong. Please try again or contact support if the problem persists.";
};

/**
 * Custom error handler function for use in try/catch blocks
 */
export const handleError = (
  error: unknown,
  context?: ErrorContext,
  onError?: (error: Error, message: string) => void
): { error: Error; message: string } => {
  const normalizedError =
    error instanceof Error
      ? error
      : new Error(typeof error === "string" ? error : "Unknown error");

  // Log the error
  logError(normalizedError, context);

  // Get user-friendly message
  const message = getUserFriendlyMessage(normalizedError);

  // Call custom error handler if provided
  if (onError) {
    onError(normalizedError, message);
  }

  return { error: normalizedError, message };
};

/**
 * Error reporting service object
 */
const errorReporting = {
  init: initErrorReporting,
  logError,
  getUserFriendlyMessage,
  handleError,
};

export default errorReporting;
